/**
 * 「发一封链接信」面板：找回密码，或者不用密码直接登录。
 *
 * 两种用途共用这一块，只有标题和按钮字不同 —— 它们的流程完全一样：
 * 填邮箱 → 发信 → 同一屏结果。
 */
import { useCallback, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { IconAlert } from '../lobby/lobbyIcons.js'
import type { EmailLinkPurpose } from '../app/emailLinkToken.js'
import { EmailLinkError, requestEmailLink } from './emailLinkApi.js'
import { IconKey } from '../icons/index.js'
import { SheetShell } from './sheetShell.js'
import './account.css'

export type RequestStage = 'input' | 'working' | 'sent'

export interface EmailLinkRequestSheetProps {
  readonly purpose: EmailLinkPurpose
  onClose?(): void
  readonly initialEmail?: string
  readonly autoFocus?: boolean
  /** 注入点：测试用；默认打真接口 */
  readonly send?: (email: string, purpose: EmailLinkPurpose) => Promise<void>
  /** 初始阶段：测试用 SSR 静态渲染每一屏 */
  readonly initialStage?: RequestStage
}

export function EmailLinkRequestSheet({
  purpose,
  onClose,
  initialEmail = '',
  autoFocus = false,
  send,
  initialStage = 'input',
}: EmailLinkRequestSheetProps): ReactNode {
  const [email, setEmail] = useState(initialEmail)
  const [stage, setStage] = useState<RequestStage>(initialStage)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const reset = purpose === 'reset'

  const submit = useCallback((): void => {
    if (email.trim() === '') {
      setError('请输入邮箱')
      return
    }
    setStage('working')
    setError(null)
    const call = send ?? ((e: string, p: EmailLinkPurpose) => requestEmailLink(e, p))
    void call(email, purpose).then(
      () => setStage('sent'),
      (e: unknown) => {
        setError(e instanceof EmailLinkError || e instanceof Error ? e.message : '没能发出去，再试一次')
        setStage('input')
      },
    )
  }, [email, purpose, send])

  return (
    <SheetShell
      title={reset ? '找回密码' : '用邮件登录'}
      icon={<IconKey size={20} />}
      dismissible={stage !== 'working'}
      {...(onClose === undefined ? {} : { onClose })}
    >
      {error !== null && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      {stage === 'sent' && (
        <>
          <p className="acct-status acct-status--ok" role="status">
            邮件发出去了
          </p>
          {/**
            * ★★ 这句**必须**是「如果这个邮箱注册过」这种条件式。
            *   写成「已发送到 xxx@y.com」就等于在界面上宣告这个邮箱注册过 ——
            *   服务端为抹平存在性做的全部工作（等时补偿、静默限流、发信移出请求路径）
            *   会被这一句话全部漏回去。
            */}
          <p className="acct-say">
            如果这个邮箱注册过，30 分钟内打开信里的链接就能{reset ? '设新密码' : '登录'}。没收到就看看垃圾箱。
          </p>
          <button type="button" className="lobby-cta pressable" onClick={() => onClose?.()}>
            知道了
          </button>
        </>
      )}

      {(stage === 'input' || stage === 'working') && (
        <>
          {!reset && <p className="acct-say">不用记密码。我们发一封信，点开链接就登录。</p>}
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-link-address">
              邮箱
            </label>
            <input
              ref={inputRef}
              id="email-link-address"
              className="acct-input acct-input--text"
              type="email"
              name="email"
              value={email}
              disabled={stage === 'working'}
              autoFocus={autoFocus}
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="you@example.com"
              onChange={(e) => {
                setEmail(e.target.value)
                if (error !== null) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit()
              }}
            />
          </div>
          <p className="acct-status" aria-live="polite">
            {stage === 'working' ? '正在发送…' : ''}
          </p>
          <button type="button" className="lobby-cta pressable" disabled={stage === 'working'} onClick={submit}>
            发送链接
          </button>
        </>
      )}
    </SheetShell>
  )
}
